import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Plausibler Jahresbereich einer Datumseingabe.
 *
 * Der Adapter hängt global in beiden Apps und deckt damit Geburts-, Impf- und
 * Vertragsdaten ab. Alles außerhalb dieser Spanne ist ein Tippfehler, kein
 * Datum — und wurde bis #997 stillschweigend übernommen: Aus `03.04.202`
 * (eine Ziffer zu wenig getippt) machte `new Date(202, 3, 3)` den 03.04.0202.
 * Der Wert landete unbemerkt in Firestore und riss anschließend den
 * Excel-Export auf, weil Excel für Jahre vor 1900 keine Datumsseriennummer
 * kennt (`-620088,03712963` statt eines Datums, angezeigt als `######`).
 */
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

/** `1.2.2020`, `01.02.2020`, `1.2.20` — Tag, Monat, zwei- oder vierstelliges Jahr. */
const GERMAN_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/;

/**
 * Zweistelliges Jahr auf ein volles Jahr bringen — gleitendes Fenster am
 * aktuellen Jahrhundert: `26` ist 2026, `85` ist 1985. Ohne diese Umrechnung
 * liest `new Date(20, 3, 3)` das Jahr 20 als 1920.
 */
function expandTwoDigitYear(year: number, referenceYear: number): number {
  const century = Math.floor(referenceYear / 100) * 100;
  return year <= referenceYear % 100 ? century + year : century - 100 + year;
}

/**
 * Liest ein deutsches Datum streng — ohne die stille Übernahme von
 * Überläufen. `31.02.2020` wäre für `new Date` der 02.03.2020; hier ist es
 * kein Datum. Gibt `null` zurück, wenn die Eingabe kein deutsches Datum ist.
 */
export function parseGermanDate(
  value: string,
  referenceYear: number = new Date().getFullYear()
): Date | null {
  const match = GERMAN_DATE.exec(value.trim());
  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year =
    match[3].length === 2
      ? expandTwoDigitYear(Number(match[3]), referenceYear)
      : Number(match[3]);

  if (year < MIN_YEAR || year > MAX_YEAR) {
    return null;
  }

  const date = new Date(year, month - 1, day);
  // Überlauf abfangen: nur ein echter Kalendertag kommt unverändert zurück.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

@Injectable()
export class CustomGermanDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: unknown): string {
    if (displayFormat === 'input') {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    }
    // For all other display formats, ensure consistent German formatting
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  /**
   * Eine punktgetrennte Eingabe wird ausschließlich als deutsches Datum
   * gelesen. Ist sie das nicht, kommt ein ungültiges `Date` zurück und Material
   * meldet `matDatepickerParse` — statt wie bisher über `Date.parse` still ein
   * anderes Datum zu liefern (`03.04.202` wurde zum Jahr 202, `03.04.2020` in
   * der Fallback-Variante zum 4. März). Alles ohne Punkt geht unverändert an
   * den `NativeDateAdapter`, damit ISO-Strings weiter funktionieren.
   */
  override parse(value: string | number): Date | null {
    if (typeof value === 'string' && value.includes('.')) {
      return parseGermanDate(value) ?? new Date(NaN);
    }
    return super.parse(value);
  }
}
